import {
  Animation,
  ArcRotateCamera,
  Color3,
  GenericPad,
  HemisphericLight,
  KeyboardEventTypes,
  Mesh,
  MeshBuilder,
  Observer,
  StandardMaterial,
  Texture,
  Vector3,
  Xbox360Button,
  Xbox360Pad,
} from '@babylonjs/core';
import { Button, Control, Ellipse, TextBlock } from '@babylonjs/gui';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { Game } from '../Game';
import { GameState } from '../GameState';
import { AssetObjectConfig, MeshFactory, SkyBoxConfig } from '../MeshFactory';

export interface DecorationOptions {
  hoverScale?: boolean;
  glow?: boolean;
  ripple?: boolean;
  sparkleParticles?: boolean;
}

export class Gui {
  static primaryColor = Color3.FromHexString('#FFFFFF');
  static secondaryColor = Color3.FromHexString('#e48716');
  static accentColor = Color3.FromHexString('#494339ff');
  static backgroundColor = Color3.FromHexString('#000000');

  static backgroundAlpha = 0.75;

  static fontFamily = 'monospace';
  static fontSizeXS = '2%';
  static fontSizeS = '3%';
  static fontSizeM = '4%';
  static fontSizeL = '5%';
  static fontSizeXL = '6%';
  static fontSizeXXL = '10%';
  static fontSizeXXXL = '20%';
  static fontSizeXXXXL = '40%';

  static controlBackground = 'rgba(0, 0, 0, 0.6)';

  static async createBackButton(
    game: Game,
    toState: GameState,
    txt?: string,
    bindButtons: boolean = true,
    left: string = '-46%',
    top: string = '-44%'
  ): Promise<Button> {
    if (!txt) {
      txt = '📺';
    }

    const action = () => {
      game.sound.play('click');
      game.routeTo(toState);
    };

    const button = game.gui2D.createButton(
      'button_back_' + toState,
      txt,
      action,
      game.gui2D.ui.rootContainer,
      1,
      game.gui2D.sceneButtonColors()
    );

    button.top = top;
    button.left = left;
    button.width = '32px';
    button.height = '32px';
    button.fontSize = '32px';
    button.thickness = 0;

    if (bindButtons) {
      game.removeableObservers.push(
        game.scene.onKeyboardObservable.add((kbInfo) => {
          if (kbInfo.type === KeyboardEventTypes.KEYUP) {
            if (kbInfo.event.code === 'Escape' || kbInfo.event.code === 'Backspace') {
              action();
            }
          }
        })
      );

      game.setupGamepad((gamepad) => {
        if (gamepad instanceof Xbox360Pad) {
          game.removeableObservers.push(
            gamepad.onButtonDownObservable.add((btn) => {
              if (btn === Xbox360Button.B) {
                action();
              }
            })
          );
        } else if (gamepad instanceof GenericPad) {
          game.removeableObservers.push(
            gamepad.onButtonDownObservable.add((btn) => {
              if (btn === 1) {
                action();
              }
            })
          );
        }
      });
    }

    return button;
  }

  static async createPauseButton(game: Game): Promise<Button> {
    const button = game.gui2D.createButton(
      'button_pause',
      '🥯',
      () => {
        game.pause();
      },
      game.gui2D.ui.rootContainer,
      1,
      game.gui2D.sceneButtonColors()
    );

    button.top = '-46%';
    button.left = '42%';
    button.width = '32px';
    button.height = '32px';
    button.fontSize = '32px';
    button.thickness = 0;
    return button;
  }

  static async createDefaultScene(game: Game, skyBox?: SkyBoxConfig, subTitleText?: string) {
    game.scene.activeCamera = new ArcRotateCamera('default_camera', -Math.PI / 2, Math.PI / 2, 10, Vector3.Zero());

    const light = new HemisphericLight('default_light', new Vector3(0.5, 1.0, 0.5), game.scene);

    if (skyBox) {
      skyBox.rotationSpeed = skyBox.rotationSpeed || 0.00001;
      await MeshFactory.createSkyBox(skyBox, 'default_skybox', game.scene);
    }

    const logoPlane = MeshBuilder.CreatePlane('title', { width: 120, height: 20 }, game.scene);
    logoPlane.position = new Vector3(0, 52, 150);
    logoPlane.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const logoMaterial = new StandardMaterial('logoMaterial', game.scene);
    const logoTexture = new Texture('assets/image/logo.png', game.scene);
    logoTexture.hasAlpha = true;
    logoMaterial.diffuseTexture = logoTexture;
    logoMaterial.opacityTexture = logoTexture;
    logoMaterial.emissiveTexture = logoTexture;
    logoMaterial.disableLighting = true;
    logoMaterial.backFaceCulling = false;
    logoPlane.material = logoMaterial;

    const pulseAnimation = new Animation('titlePulse', 'scaling', 30, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
    pulseAnimation.setKeys([
      { frame: 0, value: new Vector3(1, 1, 1) },
      { frame: 90, value: new Vector3(0.95, 0.95, 0.95) },
      { frame: 180, value: new Vector3(1, 1, 1) },
    ]);
    logoPlane.animations.push(pulseAnimation);
    game.scene.beginAnimation(logoPlane, 0, 180, true, 2);

    if (subTitleText) {
      const title = Gui.createTitle(subTitleText);
      title.top = '-23%';
      game.gui2D.ui.addControl(title);
    }
  }

  static createTitle(titleText: string): TextBlock {
    const title = new TextBlock('title', titleText);
    title.color = Gui.primaryColor.toHexString();
    title.fontSize = Gui.fontSizeXL;
    title.fontFamily = Gui.fontFamily;
    title.fontWeight = 'bold';
    title.alpha = 0.7;
    title.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    title.outlineWidth = 4;
    title.outlineColor = Gui.secondaryColor.toHexString();
    return title;
  }

  static async createAnimatedPlayer(game: Game, animationName: string, position: Vector3, loop: boolean = false): Promise<Mesh> {
    // Load player asset
    const playerAsset = new Asset('player', AssetType.MESH, 'assets/player/', 'player.glb');
    await game.assetFactory.load([playerAsset]);

    const playerAssetObj = new AssetObject('player').withClone(true);
    const config = AssetObjectConfig.builder()
      .object(playerAssetObj)
      .name('player_mesh')
      .scale(0.01)
      .position(position)
      .rotation(new Vector3(0, Math.PI, 0))
      .build();

    // Mock level for buildMesh (it needs level for game/scene access)
    // Create a temporary object that looks like Level enough for MeshFactory.
    const mockLevel = {
      game: game,
      scene: game.scene,
      addShadowCaster: () => {}, // Mock shadow generator
      highlights: { addMesh: () => {} }, // Mock highlights
      getAtlasByName: () => null,
      assetFactory: game.assetFactory, // crucial
      getMaterial: () => null,
    } as any;

    // Build mesh
    const mesh = config.buildMesh(mockLevel);
    MeshFactory.configureMesh(mesh, config, mockLevel);

    // Find and play animation
    if (animationName) {
      const anim = game.scene.getAnimationGroupByName(animationName);
      anim.enableBlending = true;
      anim.blendingSpeed = 0.1;

      const current = anim.start(loop, 1.0, anim.from, anim.to, false);
      if (!loop) {
        current.onAnimationEndObservable.add(() => {
          const idle = game.scene.getAnimationGroupByName('Idle');
          idle.enableBlending = true;
          idle.blendingSpeed = 0.1;
          if (idle) {
            idle.start(true);
          }
        });
      }
    }
    mesh.lookAt(game.scene.activeCamera.position);
    return mesh;
  }

  static async setDefaultShadow(control: Control) {
    control.shadowColor = Gui.backgroundColor.toHexString();
    control.shadowBlur = 1;
    control.shadowOffsetX = 1;
    control.shadowOffsetY = 1;
  }

  static async decorateElement(game: Game, control: Control, options: DecorationOptions = {}) {
    const defaultOptions: DecorationOptions = {
      hoverScale: true,
      glow: true,
      ripple: true,
      sparkleParticles: true,
      ...options,
    };

    if (defaultOptions.hoverScale) {
      const originalScaleX = control.scaleX;
      const originalScaleY = control.scaleY;

      control.onPointerEnterObservable.add(() => {
        game.scene.beginDirectAnimation(
          control,
          [
            Gui.createScaleAnimation('scaleX', originalScaleX, originalScaleX * 1.05),
            Gui.createScaleAnimation('scaleY', originalScaleY, originalScaleY * 1.05),
          ],
          0,
          10,
          false,
          2
        );
      });

      control.onPointerOutObservable.add(() => {
        game.scene.beginDirectAnimation(
          control,
          [
            Gui.createScaleAnimation('scaleX', control.scaleX, originalScaleX),
            Gui.createScaleAnimation('scaleY', control.scaleY, originalScaleY),
          ],
          0,
          10,
          false,
          2
        );
      });
    }

    if (defaultOptions.glow) {
      const originalShadowColor = control.shadowColor;
      const originalShadowBlur = control.shadowBlur;

      control.onPointerEnterObservable.add(() => {
        control.shadowColor = Gui.secondaryColor.toHexString();
        control.shadowBlur = 20;
      });

      control.onPointerOutObservable.add(() => {
        control.shadowColor = originalShadowColor;
        control.shadowBlur = originalShadowBlur;
      });
    }

    if (defaultOptions.ripple && (control as any).addControl) {
      control.onPointerDownObservable.add(() => {
        const ripple = new Ellipse('ripple');
        ripple.width = '10px';
        ripple.height = '10px';
        ripple.color = 'transparent';
        ripple.background = Gui.secondaryColor.toHexString();
        ripple.alpha = 0.5;
        ripple.isHitTestVisible = false;
        ripple.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        ripple.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;

        (control as any).addControl(ripple);

        const animAlpha = new Animation('rippleAlpha', 'alpha', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animAlpha.setKeys([
          { frame: 0, value: 0.5 },
          { frame: 20, value: 0 },
        ]);

        const animScaleX = new Animation('rippleScaleX', 'scaleX', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animScaleX.setKeys([
          { frame: 0, value: 1 },
          { frame: 20, value: 10 },
        ]);

        const animScaleY = new Animation('rippleScaleY', 'scaleY', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animScaleY.setKeys([
          { frame: 0, value: 1 },
          { frame: 20, value: 10 },
        ]);

        game.scene.beginDirectAnimation(ripple, [animAlpha, animScaleX, animScaleY], 0, 20, false, 1, () => {
          ripple.dispose();
        });
      });
    }

    if (defaultOptions.sparkleParticles && (control as any).addControl) {
      let lastSparkleTime = 0;
      let sparkleInterval = 100 + Math.random() * 700; // 0.1s to 0.8s

      const observer = game.scene.onBeforeRenderObservable.add(() => {
        if (!control.isVisible || control.alpha === 0 || (control as any).isDisposed) {
          return;
        }

        const now = performance.now();
        if (now - lastSparkleTime > sparkleInterval) {
          lastSparkleTime = now;
          sparkleInterval = 100 + Math.random() * 700;

          const star = new TextBlock('star', '✦');
          star.color = '#FFD700'; // Yellow
          star.fontSize = '12px';
          star.alpha = 0;
          star.isHitTestVisible = false;

          star.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
          star.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;

          const edge = Math.floor(Math.random() * 4);
          const measure = (control as any)._currentMeasure;
          const w = measure?.width || (control as any).widthInPixels || 150;
          const h = measure?.height || (control as any).heightInPixels || 40;

          let left = 0;
          let top = 0;

          if (edge === 0) {
            // Top
            left = Math.random() * w - w / 2;
            top = -(h / 2) - 6;
          } else if (edge === 1) {
            // Bottom
            left = Math.random() * w - w / 2;
            top = h / 2 - 6;
          } else if (edge === 2) {
            // Left
            left = -(w / 2) - 6;
            top = Math.random() * h - h / 2;
          } else {
            // Right
            left = w / 2 - 6;
            top = Math.random() * h - h / 2;
          }

          star.leftInPixels = left;
          star.topInPixels = top;
          star.shadowColor = Gui.secondaryColor.toHexString();
          star.shadowBlur = 10;

          (control as any).addControl(star);

          const animAlpha = new Animation('starAlpha', 'alpha', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
          animAlpha.setKeys([
            { frame: 0, value: 0 },
            { frame: 30, value: 0.8 },
            { frame: 60, value: 0 },
          ]);

          const animRot = new Animation('starRot', 'rotation', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
          animRot.setKeys([
            { frame: 0, value: 0 },
            { frame: 60, value: Math.PI },
          ]);

          game.scene.beginDirectAnimation(star, [animAlpha, animRot], 0, 60, false, 1, () => {
            star.dispose();
          });
        }
      });

      control.onDisposeObservable.add(() => {
        game.scene.onBeforeRenderObservable.remove(observer);
      });
    }
  }

  static createScaleAnimation(property: string, from: number, to: number): Animation {
    const anim = new Animation(property + 'Anim', property, 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
    anim.setKeys([
      { frame: 0, value: from },
      { frame: 10, value: to },
    ]);
    return anim;
  }
}

export class MenuNavigation {
  private game: Game;
  private controls: Button[] = [];
  private selectedIndex: number = 0;
  private inputTimer: number = 0;
  private activeGamepad: any = null;
  private buttonAPressed: boolean = false;
  private isFirstFrame: boolean = true;
  private observers: Observer<any>[] = [];

  constructor(game: Game) {
    this.game = game;
  }

  addControl(control: Button) {
    const index = this.controls.length;
    this.controls.push(control);

    control.pointerEnterAnimation = () => {
      this.selectedIndex = index;
      this.updateSelection();
    };
    control.pointerOutAnimation = () => {
      // keep selected visual state
    };
  }

  start() {
    this.updateSelection();

    this.observers.push(
      this.game.scene.onKeyboardObservable.add((kbInfo) => {
        if (kbInfo.type === KeyboardEventTypes.KEYDOWN) {
          let moved = false;
          if (kbInfo.event.code === 'ArrowDown' || kbInfo.event.code === 'ArrowRight') {
            if (this.selectedIndex < this.controls.length - 1) {
              this.selectedIndex++;
              moved = true;
            }
          } else if (kbInfo.event.code === 'ArrowUp' || kbInfo.event.code === 'ArrowLeft') {
            if (this.selectedIndex > 0) {
              this.selectedIndex--;
              moved = true;
            }
          }
          if (moved) {
            this.game.sound.play('click');
            this.updateSelection();
          }
        } else if (kbInfo.type === KeyboardEventTypes.KEYUP) {
          if (kbInfo.event.code === 'Space' || kbInfo.event.code === 'Enter') {
            this.confirmSelection();
          }
        }
      })
    );

    this.game.setupGamepad((gamepad) => {
      this.activeGamepad = gamepad;
    });

    this.observers.push(this.game.scene.onBeforeRenderObservable.add(() => this.updateGamepadInput()));

    // Track observers so they can be removed on routeTo
    this.observers.forEach((obs) => this.game.removeableObservers.push(obs));
  }

  private updateSelection() {
    this.controls.forEach((btn, idx) => {
      if (idx === this.selectedIndex) {
        btn.scaleX = 1.05;
        btn.scaleY = 1.05;
        btn.thickness = 3;
      } else {
        btn.scaleX = 1.0;
        btn.scaleY = 1.0;
        btn.thickness = 1;
      }
    });
  }

  private confirmSelection() {
    const selected = this.controls[this.selectedIndex];
    if (!selected) return;

    // Simulate pointer up to trigger the button's action
    selected.onPointerUpObservable.notifyObservers(null as any);
  }

  private updateGamepadInput() {
    const pad = this.activeGamepad?.browserGamepad;
    if (!pad) return;

    let y = pad.axes[1] || 0;
    let x = pad.axes[0] || 0;

    // 12: d-pad up, 13: d-pad down, 14: d-pad left, 15: d-pad right
    if (pad.buttons[12]?.pressed || pad.buttons[14]?.pressed) y = -1;
    if (pad.buttons[13]?.pressed || pad.buttons[15]?.pressed) y = 1;

    const now = Date.now();
    if (now - this.inputTimer > 200) {
      let moved = false;
      if (y < -0.5 || x < -0.5) {
        if (this.selectedIndex > 0) {
          this.selectedIndex--;
          moved = true;
        }
      } else if (y > 0.5 || x > 0.5) {
        if (this.selectedIndex < this.controls.length - 1) {
          this.selectedIndex++;
          moved = true;
        }
      }

      if (moved) {
        this.game.sound.play('click');
        this.updateSelection();
        this.inputTimer = now;
      }
    }

    const aPressed = pad.buttons[0]?.pressed;
    if (this.isFirstFrame) {
      this.buttonAPressed = aPressed || false;
      this.isFirstFrame = false;
    } else {
      if (aPressed && !this.buttonAPressed) {
        this.confirmSelection();
      }
      this.buttonAPressed = aPressed || false;
    }
  }
}
